import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Pencil, Trash2, UserPlus, PauseCircle, PlayCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { MemberDialog, EMPTY_MEMBER, memberToForm } from "@/components/admin/MemberDialog";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { api, downloadFile, fmtDate, formatErr, money } from "@/lib/api";

const Icon = ({ on, label, testId, children }) => (
  <button onClick={on} aria-label={label} title={label} data-testid={testId} className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary">{children}</button>
);

const LinkDialog = ({ link, onClose }) => (
  <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="member-setup-link-dialog">
      <DialogHeader><DialogTitle>Portal setup link</DialogTitle><DialogDescription>Single-use link (valid 7 days) for the member to create their password.</DialogDescription></DialogHeader>
      <div className="rounded-xl bg-secondary p-4 text-xs font-mono break-all" data-testid="member-setup-link-value">{link}</div>
    </DialogContent>
  </Dialog>
);

export default function Members() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [link, setLink] = useState("");
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get("/admin/members").then((r) => setMembers(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div data-testid="admin-members-page">
      <PageTitle title="Members" sub="Active and suspended members, intro usage and manual members.">
        <button onClick={() => downloadFile("/admin/export/members", "members.csv")} className="inline-flex h-10 items-center gap-2 px-4 rounded-full border border-border font-semibold text-sm hover:bg-secondary" data-testid="members-export-button"><Download className="h-4 w-4" />Export CSV</button>
        <button onClick={() => setEditing({ ...EMPTY_MEMBER })} className="inline-flex h-10 items-center gap-2 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm" data-testid="add-member-button"><UserPlus className="h-4 w-4" />Add Member</button>
      </PageTitle>
      {editing && <MemberDialog member={editing} onClose={() => setEditing(null)} onSaved={(l) => { setEditing(null); if (l) setLink(l); load(); }} />}
      <LinkDialog link={link} onClose={() => setLink("")} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {deleting?.contact.full_name}?</AlertDialogTitle><AlertDialogDescription>This permanently removes the member, their login, introductions, add-on requests and ID files.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => act(() => api.delete(`/admin/members/${deleting.id}`), "Member deleted")} data-testid="member-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <Table data-testid="members-table">
          <TableHeader>
            <TableRow><TableHead>Member</TableHead><TableHead>Tier</TableHead><TableHead>Status</TableHead><TableHead>Cash paid</TableHead><TableHead>Since</TableHead><TableHead className="w-48">Introductions</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground" data-testid="members-empty">No members yet.</TableCell></TableRow>}
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell>
                  <Link to={`/admin/applicants/${m.id}`} className="font-bold hover:text-primary" data-testid={`member-link-${m.id}`}>{m.contact.full_name}</Link>
                  <p className="text-xs text-muted-foreground">{m.contact.email} · {m.contact.country}{m.source === "manual" ? " · manual" : ""}</p>
                </TableCell>
                <TableCell className="font-semibold">{m.tier_name}</TableCell>
                <TableCell><StatusBadge status={m.status} testId={`member-status-${m.id}`} /></TableCell>
                <TableCell>{m.payment ? money(m.payment.amount) : "-"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{fmtDate(m.activated_at)}</TableCell>
                <TableCell><div className="flex items-center gap-3"><Progress value={(m.used / m.limit) * 100} className="h-2" /><span className="text-sm font-bold whitespace-nowrap">{m.used}/{m.limit}</span></div></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Icon on={() => setEditing(memberToForm(m))} label="Edit" testId={`member-edit-${m.id}`}><Pencil className="h-4 w-4" /></Icon>
                    {m.status === "Active Member"
                      ? <Icon on={() => act(() => api.post(`/admin/members/${m.id}/status`, { status: "Suspended" }), "Member suspended")} label="Suspend" testId={`member-suspend-${m.id}`}><PauseCircle className="h-4 w-4 text-amber-500" /></Icon>
                      : <Icon on={() => act(() => api.post(`/admin/members/${m.id}/status`, { status: "Active Member" }), "Member reactivated")} label="Reactivate" testId={`member-reactivate-${m.id}`}><PlayCircle className="h-4 w-4 text-primary" /></Icon>}
                    <Icon on={() => setDeleting(m)} label="Delete" testId={`member-delete-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Icon>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
